import React, { useState, useEffect, useRef } from 'react';
import apiFetch, { aiApiFetch } from '../utils/apiFetch';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Speedometer from 'react-d3-speedometer';
import HybridRendererSelector from '../components/HybridRendererSelector';
import mammoth from 'mammoth';
import { FiUpload, FiFileText, FiCheckCircle, FiBriefcase, FiUser, FiUsers, FiAlertTriangle, FiTrash2, FiSearch, FiRotateCw } from 'react-icons/fi';
import '../styles/NewUser.css';
import '../styles/SkillAssessment.css';
import '../styles/InterviewEffectiveness.css';

function NewUser() {
  const [selectedSkill, setSelectedSkill] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [candidateFile, setCandidateFile] = useState(null);
  const [jdContent, setJdContent] = useState('');
  const [candidateContent, setCandidateContent] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [skillData, setSkillData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const intExt = ['External', 'Internal to Iris'];
  const [lastCandidateContent, setLastCandidateContent] = useState('');
  const [lastJDContent, setLastJDContent] = useState('');
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [effectivenessResult, setEffectivenessResult] = useState(null);
  const ASSESS_DB_API_ENDPOINT = process.env.REACT_APP_DB_API_URL;
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const jdInputRef = useRef(null);
  const candidateInputRef = useRef(null);
  const [interviewEffectiveness, setInterviewEffectiveness] = useState(null);
  const [shortlistingProbability, setShortlistingProbability] = useState(null);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState('results');
  const [isDraggingJd, setIsDraggingJd] = useState(false);
  const [isDraggingTranscript, setIsDraggingTranscript] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [useStructuredUI, setUseStructuredUI] = useState(true);

  // ADD THESE MISSING STATE VARIABLES:
  const [duplicateError, setDuplicateError] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [existingAssessment, setExistingAssessment] = useState(null);

  // Add new state for content duplicates
  const [contentDuplicateError, setContentDuplicateError] = useState(null);
  const [showContentDuplicateModal, setShowContentDuplicateModal] = useState(false);
  const [existingContentEntries, setExistingContentEntries] = useState([]);

  // New state variables for the additional fields
  const [projectName, setProjectName] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [interviewerName, setInterviewerName] = useState('');

  // Add this with your other useState declarations
  const [viewingExistingAssessment, setViewingExistingAssessment] = useState(null);
  

  const validFileTypes = ['text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  const getFillColor = (performance) => (performance <= 50 ? '#FF9933' : '#00CC00'); // Amber for <=50, Green for >50

  const getRecommendationText = (score, type) => {
    if (type === 'shortlisting') {
      if (score >= 75) return "Strong candidate - Highly recommended for shortlisting";
      if (score >= 50) return "Potential match - Consider additional assessment";
      return "Low alignment - May not be suitable for this role";
    } else {
      if (score >= 75) return "Excellent interview coverage of required skills";
      if (score >= 50) return "Adequate interview coverage with some gaps";
      return "Insufficient interview coverage of key requirements";
    }
  };

   // Check if all required fields are filled
  const areDetailsComplete = () => {
    return projectName.trim() !== '' && 
           candidateName.trim() !== '' && 
           interviewerName.trim() !== '' && 
           selectedSkill !== '';
  };

  const handleSkillSelect = (skill) => {
    setSelectedSkill(skill);
    setShowDropdown(false);
    setSkillData(skill);

    if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  // Handle input changes for the new fields
  const handleInputChange = (field, value) => {
    switch (field) {
      case 'project':
        setProjectName(value);
        break;
      case 'candidate':
        setCandidateName(value);
        break;
      case 'interviewer':
        setInterviewerName(value);
        break;
      default:
        break;
    }

    // Check if all fields are complete after any field change
    if (areDetailsComplete() && currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleJdFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage('Please upload a file in .txt or .docx format.');
      setJdFile(null);
      setJdContent('');
      return;
    }

    setErrorMessage('');
    setJdFile(file);

    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        mammoth.extractRawText({ arrayBuffer })
          .then(result => {
            setJdContent(result.value);
            // Always move to at least step 2 after successful job description upload
            setCurrentStep(Math.max(currentStep, 2));
          })
          .catch(err => {
            console.error('Error reading JD file:', err);
            setErrorMessage('Error processing the document file.');
          });
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        setJdContent(e.target.result);
        // Always move to at least step 2 after successful job description upload
        setCurrentStep(Math.max(currentStep, 2));
      };
      reader.readAsText(file);
    }
  };

  const handleCandidateFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage('Please upload a file in .txt or .docx format.');
      setCandidateFile(null);
      setCandidateContent('');
      return;
    }

    setErrorMessage('');
    setCandidateFile(file);

    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        mammoth.extractRawText({ arrayBuffer })
          .then(result => {
            setCandidateContent(result.value);
            if (currentStep === 3 && selectedSkill) setCurrentStep(4);
          })
          .catch(err => {
            console.error('Error reading Candidate file:', err);
            setErrorMessage('Error processing the document file.');
          });
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCandidateContent(e.target.result);
        if (currentStep === 3 && selectedSkill) setCurrentStep(4);
      };
      reader.readAsText(file);
    }
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === 'jd') setIsDraggingJd(true);
    else setIsDraggingTranscript(true);
  };

  const handleDragLeave = (type) => {
    if (type === 'jd') setIsDraggingJd(false);
    else setIsDraggingTranscript(false);
  };

  const handleDrop = async (e, type) => {
    e.preventDefault();

    if (type === 'jd') {
      setIsDraggingJd(false);
    } else {
      setIsDraggingTranscript(false);
    }

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage(`Please upload a file in .txt or .docx format.`);
      return;
    }

    setErrorMessage('');

    if (type === 'jd') {
      setJdFile(file);
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      jdInputRef.current.files = dataTransfer.files;

      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target.result;
          mammoth.extractRawText({ arrayBuffer })
            .then(result => {
              setJdContent(result.value);
              if (currentStep === 1) setCurrentStep(2);
            })
            .catch(err => {
              console.error('Error reading JD file:', err);
              setErrorMessage('Error processing the document file.');
            });
        };
        reader.readAsArrayBuffer(file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          setJdContent(e.target.result);
          if (currentStep === 1) setCurrentStep(2);
        };
        reader.readAsText(file);
      }
    } else {
      setCandidateFile(file);
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      candidateInputRef.current.files = dataTransfer.files;

      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const reader = new FileReader();
        reader.onload = (e) => {
          const arrayBuffer = e.target.result;
          mammoth.extractRawText({ arrayBuffer })
            .then(result => {
              setCandidateContent(result.value);
              if (currentStep === 3 && selectedSkill) setCurrentStep(4);
            })
            .catch(err => {
              console.error('Error reading Candidate file:', err);
              setErrorMessage('Error processing the document file.');
            });
        };
        reader.readAsArrayBuffer(file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          setCandidateContent(e.target.result);
          if (currentStep === 3 && selectedSkill) setCurrentStep(4);
        };
        reader.readAsText(file);
      }
    }
  };

  const removeFile = (type) => {
    if (type === 'jd') {
      setJdFile(null);
      setJdContent('');
      if (jdInputRef.current) jdInputRef.current.value = null;
      if (currentStep > 1) setCurrentStep(1);
    } else {
      setCandidateFile(null);
      setCandidateContent('');
      if (candidateInputRef.current) candidateInputRef.current.value = null;
      if (currentStep > 3) setCurrentStep(3);
    }
  };

const checkForDuplicates = async () => {
  console.log('🔍 Starting duplicate check...');
  
  try {
    // Only validate content fields (jobDescription and transcript)
    if (!jdContent?.trim() || !candidateContent?.trim()) {
      console.error('Missing content for duplicate check');
      return true; // Proceed anyway
    }
    
    console.log('🌐 Making API call to check duplicates...');
    console.log('📊 API Endpoint:', `${ASSESS_DB_API_ENDPOINT}/api/assessment/check-duplicates`);
    
    const requestPayload = {
      project: projectName?.trim() || 'Project',
      candidate: candidateName?.trim() || 'Candidate',
      interviewer: interviewerName?.trim() || 'Interviewer',
      jobDescription: jdContent.trim(),
      transcript: candidateContent.trim()
    };
    
    console.log('📋 Request payload:', {
      project: requestPayload.project,
      candidate: requestPayload.candidate,
      interviewer: requestPayload.interviewer,
      jobDescription: `${requestPayload.jobDescription.length} characters`,
      transcript: `${requestPayload.transcript.length} characters`
    });
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    const checkResponse = await apiFetch('/api/assessment/check-duplicates', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: JSON.stringify(requestPayload),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    console.log('📡 API Response status:', checkResponse.status);
    console.log('📡 API Response ok:', checkResponse.ok);
    
    // Handle 409 Conflict (Duplicates Found)
    if (checkResponse.status === 409) {
      console.log('🚫 409 Conflict - Duplicates detected');
      
      const duplicateData = await checkResponse.json();
      console.log('📊 Duplicate data:', duplicateData);
      
      // Handle exact duplicates
      if (duplicateData.type === 'exact_duplicate') {
        console.log('🎯 Setting exact duplicate modal...');
        
        setDuplicateError({
          ...duplicateData,
          enhancedMessage: `This assessment was already completed on ${new Date(duplicateData.existingEntry.create_date).toLocaleDateString()}.`,
          actionOptions: [
            { key: 'view', label: 'View Existing Assessment', action: 'view' },
            { key: 'update', label: 'Update with New Results', action: 'update' },
            { key: 'cancel', label: 'Cancel', action: 'cancel' }
          ]
        });
        setExistingAssessment(duplicateData.existingEntry);
        setShowDuplicateModal(true);
        
        console.log('🚫 Exact duplicate found - Returning FALSE to stop assessment');
        return false; // Stop processing
        
      } 
      // Handle content duplicates  
      else if (duplicateData.type === 'content_duplicate') {
        console.log('🎯 Setting content duplicate modal...');
        
        setContentDuplicateError({
          ...duplicateData,
          enhancedMessage: `This job description and transcript combination has been processed ${duplicateData.totalMatches} time(s) before:`,
          actionOptions: [
            { key: 'correct', label: 'Update Existing Entry Names', action: 'correct' },
            { key: 'proceed', label: 'Proceed as New Assessment', action: 'proceed' },
            { key: 'view', label: 'View Existing Assessments', action: 'view' },
            { key: 'cancel', label: 'Cancel', action: 'cancel' }
          ]
        });
        setExistingContentEntries(duplicateData.existingEntries);
        setShowContentDuplicateModal(true);
        
        console.log('🚫 Content duplicate found - Returning FALSE to stop assessment');
        return false; // Stop processing
      }
    }
    
    // Handle other non-200 responses
    if (!checkResponse.ok) {
      console.error('API response not ok:', checkResponse.status, checkResponse.statusText);
      
      // Try to get error details
      try {
        const errorData = await checkResponse.text();
        console.error('Error response body:', errorData);
      } catch (e) {
        console.error('Could not parse error response:', e);
      }
      
      return true; // Proceed anyway if check fails
    }
    
    // Handle successful response (200)
    const duplicateData = await checkResponse.json();
    console.log('📊 API Response data:', duplicateData);
    
    console.log('✅ No duplicates found, proceeding with assessment');
    return true; // No duplicates found, proceed with assessment
    
  } catch (error) {
    console.error('❌ Duplicate check failed:', error);
    
    if (error.name === 'AbortError') {
      console.error('Request timed out');
      setError('Duplicate check timed out. Please try again.');
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.error('Network error during duplicate check');
      setError('Network error during duplicate check. Please check your connection.');
    } else {
      console.error('Unexpected error:', error);
      setError('Error checking for duplicates. Proceeding with assessment.');
    }
    
    return true; // Proceed anyway if check fails
  }
};

  // Add client-side content hash function
  const createClientContentHash = async (jobDescription, transcript) => {
    const combined = `${jobDescription.trim()}|||${transcript.trim()}`;
    const encoder = new TextEncoder();
    const data = encoder.encode(combined);
    //const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleAssessClick = async () => {
  console.log('🚀 Assessment button clicked');
  
  if (isLoading) {
    console.log('⏳ Already loading, skipping...');
    return;
  }
  
  setIsButtonClicked(true);
  setError(null);
  setDuplicateError(null);
  setContentDuplicateError(null);

  // Always advance to step 5 when clicking the assess button
  setCurrentStep(5);

  // STEP 1: Check for duplicates FIRST
  console.log('📋 Step 1: Calling checkForDuplicates...');
  const shouldProceed = await checkForDuplicates();
  
  console.log('📋 checkForDuplicates returned:', shouldProceed);
  
  if (!shouldProceed) {
    console.log('🚫 Assessment STOPPED - Duplicates found');
    console.log('🔍 Current modal states:', {
      showDuplicateModal,
      showContentDuplicateModal,
      duplicateError: !!duplicateError,
      contentDuplicateError: !!contentDuplicateError
    });
    // Don't set loading to false here - let the modals handle the UI
    return; // This should stop execution and show the modal
  }

  console.log('✅ No duplicates found, proceeding with AI assessment...');

  // STEP 2: Only do AI assessment if no duplicates found
  setIsLoading(true);
  setLastCandidateContent(candidateContent);
  setLastJDContent(jdContent);

  const dataToSend = {
    transcriptText: candidateContent,
    jobDescriptionText: jdContent,
  };

  try {
    console.time('api-parallel-calls');

    // Make both API calls in parallel
    const [skillResponse, effectivenessResponse] = await Promise.all([
      aiApiFetch('/evaluateskill', {
        method: 'POST',
        body: JSON.stringify(dataToSend),
      }),
      aiApiFetch('/evaluateeffectiveness', {
        method: 'POST',
        body: JSON.stringify(dataToSend),
      })
    ]);

    // Check if either API call failed
    if (!skillResponse.ok) {
      const errorData = await skillResponse.json();
      throw new Error(`Skills Assessment API error! status: ${skillResponse.status}, message: ${errorData.error || skillResponse.statusText}`);
    }

    if (!effectivenessResponse.ok) {
      const errorData = await effectivenessResponse.json();
      throw new Error(`Interview Effectiveness API error! status: ${effectivenessResponse.status}, message: ${errorData.error || effectivenessResponse.statusText}`);
    }

    // Parse both JSON responses in parallel
    const [skillData, effectivenessData] = await Promise.all([
      skillResponse.json(),
      effectivenessResponse.json()
    ]);

    console.timeEnd('api-parallel-calls');

    // Check for errors in the response data
    if (skillData.error) {
      throw new Error(skillData.error);
    }

    if (effectivenessData.error) {
      throw new Error(effectivenessData.error);
    }

    // Process Skills Assessment results
    const resultText = skillData.result;
    setAssessmentResult(resultText);

    // Extract structured data if available
    let skillProbability = 0; // Default value
    let structuredSkillData = null;
      
      try {
        const parsedSkillData = JSON.parse(resultText);
        if (parsedSkillData.structured && parsedSkillData.structured.shortlistingProbability) {
          skillProbability = parsedSkillData.structured.shortlistingProbability;
          structuredSkillData = parsedSkillData.structured;
        } else if (parsedSkillData.markdown) {
          // Extract from markdown if structured data doesn't contain it
          const cleanedInput = parsedSkillData.markdown.replace(/\*\*/g, '').trim();
          const shortlistingProbabilityRegex = /Candidate Shortlisting Probability:\s*(\d+(\.\d+)?%)/;
          const shortlistingProbabilityMatch = cleanedInput.match(shortlistingProbabilityRegex);
          if (shortlistingProbabilityMatch && shortlistingProbabilityMatch[1]) {
            skillProbability = parseFloat(shortlistingProbabilityMatch[1].replace(/%/, ''));
          }
        }
      } catch (e) {
        // Fallback to regex extraction from plain text
        const cleanedInput = resultText.replace(/\*\*/g, '').trim();
        const shortlistingProbabilityRegex = /Candidate Shortlisting Probability:\s*(\d+(\.\d+)?%)/;
        const shortlistingProbabilityMatch = cleanedInput.match(shortlistingProbabilityRegex);
        if (shortlistingProbabilityMatch && shortlistingProbabilityMatch[1]) {
          skillProbability = parseFloat(shortlistingProbabilityMatch[1].replace(/%/, ''));
        }
      }
      
      setShortlistingProbability(skillProbability);

      // Process Interview Effectiveness results
      const ieResultText = effectivenessData.result;
      setEffectivenessResult(ieResultText);

      // Extract effectiveness data
      let effectivenessProbability = 0; // Default value
      let structuredEffectivenessData = null;
      
      try {
        const parsedEffectivenessData = JSON.parse(ieResultText);
        if (parsedEffectivenessData.structured && 
            parsedEffectivenessData.structured.effectivenessMetrics && 
            parsedEffectivenessData.structured.effectivenessMetrics.overallEffectiveness) {
          effectivenessProbability = parsedEffectivenessData.structured.effectivenessMetrics.overallEffectiveness;
          structuredEffectivenessData = parsedEffectivenessData.structured;
        } else if (parsedEffectivenessData.markdown) {
          // Extract from markdown if structured data doesn't contain it
          const cleanedInput = parsedEffectivenessData.markdown.replace(/\*\*/g, '').trim();
          const effectivenessRegex = /Interview Effectiveness:\s*(\d+(\.\d+)?%)/;
          const interviewEffectivenessMatch = cleanedInput.match(effectivenessRegex);
          if (interviewEffectivenessMatch && interviewEffectivenessMatch[1]) {
            effectivenessProbability = parseFloat(interviewEffectivenessMatch[1].replace(/%/, ''));
          }
        }
      } catch (e) {
        // Fallback to regex extraction from plain text
        const cleanedInput = ieResultText.replace(/\*\*/g, '').trim();
        const effectivenessRegex = /Interview Effectiveness:\s*(\d+(\.\d+)?%)/;
        const interviewEffectivenessMatch = cleanedInput.match(effectivenessRegex);
        if (interviewEffectivenessMatch && interviewEffectivenessMatch[1]) {
          effectivenessProbability = parseFloat(interviewEffectivenessMatch[1].replace(/%/, ''));
        }
      }
      
      setInterviewEffectiveness(effectivenessProbability);

      // STEP 3: Save to database (this should now work without duplicate issues)
    const insertData = {
      project: projectName,
      candidate: candidateName,
      interviewer: interviewerName,
      assessment_probability: resultText,
      assessment_effectiveness: ieResultText,
      shortlisting_probability: skillProbability,
      interview_effectiveness: effectivenessProbability,
      intext_data: selectedSkill,
      jobDescription: jdContent,
      transcript: candidateContent
    };

    const insertResponse = await apiFetch('/api/assessment', {
      method: 'POST',
      body: JSON.stringify(insertData),
    });

    if (insertResponse.ok) {
      console.log('Assessment saved successfully');
    } else {
      const errorData = await insertResponse.json();
      console.warn('Save failed:', errorData.message);
      // Don't treat as fatal error since assessment is complete
    }

  } catch (error) {
    console.error('API call error:', error);
    setError(error.message);
  } finally {
    setIsLoading(false);
  }
};

  const handleReset = () => {
    // Reset core assessment data
    setSelectedSkill('');
    setJdFile(null);
    setCandidateFile(null);
    setJdContent('');
    setCandidateContent('');
    setSkillData(null);
    setAssessmentResult(null);
    setEffectivenessResult(null);
    
    // Reset the new form fields (MISSING!)
    setProjectName('');
    setCandidateName('');
    setInterviewerName('');
    
    // Reset all error states (MISSING!)
    setErrorMessage('');
    setError(null);
    setDuplicateError(null);
    
    // Reset UI states (MISSING!) 
    setIsButtonClicked(false);
    setShowDropdown(false);
    setShowDuplicateModal(false);
    setExistingAssessment(null);
    setSelectedTab('results');
    
    // Reset drag states (MISSING!)
    setIsDraggingJd(false);
    setIsDraggingTranscript(false);
    
    // Reset gauges/graphs (EXISTING - GOOD!)
    setShortlistingProbability(null);
    setInterviewEffectiveness(null);
    
    // Reset loading and step states
    setIsLoading(false);
    setCurrentStep(1);
    
    // Reset previous content tracking (MISSING!)
    setLastCandidateContent('');
    setLastJDContent('');
    
    // Reset file inputs (EXISTING - GOOD!)
    if (jdInputRef.current) {
      jdInputRef.current.value = null;
    }

    if (candidateInputRef.current) {
      candidateInputRef.current.value = null;
    }
    
    // Reset content duplicate states (ADD THIS!)
    setContentDuplicateError(null);
    setShowContentDuplicateModal(false);
    setExistingContentEntries([]);
    
    // Reset viewing state
    setViewingExistingAssessment(null);
    
    console.log('All fields and UI state reset successfully');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const isAssessButtonEnabled = projectName && candidateName && interviewerName && selectedSkill && jdContent && candidateContent;


  // Toggle between structured and markdown display
  const toggleDisplayMode = () => {
    setUseStructuredUI(!useStructuredUI);
  };

  // Add these new handler functions
  const handleUpdateExisting = async () => {
    if (!existingAssessment || !duplicateError) return;

    try {
      setIsLoading(true);
      
      const updateData = {
        assessment_probability: assessmentResult,
        assessment_effectiveness: effectivenessResult,
        shortlisting_probability: shortlistingProbability,
        interview_effectiveness: interviewEffectiveness,
        intext_data: selectedSkill
      };

      const updateResponse = await apiFetch(`/api/assessment/${existingAssessment.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(`Update failed: ${errorData.error || updateResponse.statusText}`);
      }

      setShowDuplicateModal(false);
      setDuplicateError(null);
      alert('Assessment updated successfully!');
      
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update assessment: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewExisting = (entry = null) => {
    if (entry) {
      // Show the existing assessment details in the modal
      setViewingExistingAssessment(entry);
    } else {
      // For exact duplicates, use the existingAssessment
      setViewingExistingAssessment(existingAssessment);
    }
  };

  const handleKeepBoth = () => {
    setShowDuplicateModal(false);
    setDuplicateError(null);
    alert('Current assessment results are displayed. Previous assessment remains unchanged in database.');
  };

  // Add content duplicate handlers
  const handleCorrectNames = async (existingId) => {
    // Update existing entry with correct names
    try {
      const updateResponse = await apiFetch(`/api/assessment/${existingId}`, {
        method: 'PUT',
        body: JSON.stringify({
          project: projectName,
          candidate: candidateName,
          interviewer: interviewerName,
          assessment_probability: assessmentResult,
          assessment_effectiveness: effectivenessResult,
          shortlisting_probability: shortlistingProbability,
          interview_effectiveness: interviewEffectiveness,
          intext_data: selectedSkill
        }),
      });

      if (updateResponse.ok) {
        setShowContentDuplicateModal(false);
        alert('Names updated successfully for existing assessment!');
      }
    } catch (error) {
      alert('Failed to update names: ' + error.message);
    }
  };

  const handleProceedAnyway = async () => {
    try {
      setShowContentDuplicateModal(false);
      setContentDuplicateError(null);
      
      // Now do the actual assessment since user chose to proceed
      setIsLoading(true);
      
      // Move your AI assessment logic here or call the existing assessment function
      // For now, continue with the regular assessment flow
      await proceedWithNewAssessment();
      
    } catch (error) {
      alert('Failed to proceed: ' + error.message);
    }
  };

  // Helper function to proceed with assessment
  const proceedWithNewAssessment = async () => {
    try {
      setLastCandidateContent(candidateContent);
      setLastJDContent(jdContent);

      const dataToSend = {
        transcriptText: candidateContent,
        jobDescriptionText: jdContent,
      };

      console.time('api-parallel-calls');

      // Make both API calls in parallel (existing logic)
      const [skillResponse, effectivenessResponse] = await Promise.all([
        aiApiFetch('/evaluateskill', {
          method: 'POST',
          body: JSON.stringify(dataToSend),
        }),
        aiApiFetch('/evaluateeffectiveness', {
          method: 'POST',
          body: JSON.stringify(dataToSend),
        })
      ]);

      // Process responses (copy the existing response processing logic from handleAssessClick)
      if (!skillResponse.ok) {
        const errorData = await skillResponse.json();
        throw new Error(`Skills Assessment API error! status: ${skillResponse.status}, message: ${errorData.error || skillResponse.statusText}`);
      }

      if (!effectivenessResponse.ok) {
        const errorData = await effectivenessResponse.json();
        throw new Error(`Interview Effectiveness API error! status: ${effectivenessResponse.status}, message: ${errorData.error || effectivenessResponse.statusText}`);
      }

      const [skillData, effectivenessData] = await Promise.all([
        skillResponse.json(),
        effectivenessResponse.json()
      ]);

      console.timeEnd('api-parallel-calls');

      // Process and set results (copy your existing result processing logic)
      const resultText = skillData.result;
      setAssessmentResult(resultText);

      // Extract probabilities (copy your existing extraction logic)
      let skillProbability = 0;
      let structuredSkillData = null;
      
      try {
        const parsedSkillData = JSON.parse(resultText);
        if (parsedSkillData.structured && parsedSkillData.structured.shortlistingProbability) {
          skillProbability = parsedSkillData.structured.shortlistingProbability;
          structuredSkillData = parsedSkillData.structured;
        } else if (parsedSkillData.markdown) {
          // Extract from markdown if structured data doesn't contain it
          const cleanedInput = parsedSkillData.markdown.replace(/\*\*/g, '').trim();
          const shortlistingProbabilityRegex = /Candidate Shortlisting Probability:\s*(\d+(\.\d+)?%)/;
          const shortlistingProbabilityMatch = cleanedInput.match(shortlistingProbabilityRegex);
          if (shortlistingProbabilityMatch && shortlistingProbabilityMatch[1]) {
            skillProbability = parseFloat(shortlistingProbabilityMatch[1].replace(/%/, ''));
          }
        }
      } catch (e) {
        // Fallback to regex extraction from plain text
        const cleanedInput = resultText.replace(/\*\*/g, '').trim();
        const shortlistingProbabilityRegex = /Candidate Shortlisting Probability:\s*(\d+(\.\d+)?%)/;
        const shortlistingProbabilityMatch = cleanedInput.match(shortlistingProbabilityRegex);
        if (shortlistingProbabilityMatch && shortlistingProbabilityMatch[1]) {
          skillProbability = parseFloat(shortlistingProbabilityMatch[1].replace(/%/, ''));
        }
      }
      
      setShortlistingProbability(skillProbability);

      // Process Interview Effectiveness results
      const ieResultText = effectivenessData.result;
      setEffectivenessResult(ieResultText);

      let effectivenessProbability = 0;
      let structuredEffectivenessData = null;
      
      try {
        const parsedEffectivenessData = JSON.parse(ieResultText);
        if (parsedEffectivenessData.structured && 
            parsedEffectivenessData.structured.effectivenessMetrics && 
            parsedEffectivenessData.structured.effectivenessMetrics.overallEffectiveness) {
          effectivenessProbability = parsedEffectivenessData.structured.effectivenessMetrics.overallEffectiveness;
          structuredEffectivenessData = parsedEffectivenessData.structured;
        } else if (parsedEffectivenessData.markdown) {
          // Extract from markdown if structured data doesn't contain it
          const cleanedInput = parsedEffectivenessData.markdown.replace(/\*\*/g, '').trim();
          const effectivenessRegex = /Interview Effectiveness:\s*(\d+(\.\d+)?%)/;
          const interviewEffectivenessMatch = cleanedInput.match(effectivenessRegex);
          if (interviewEffectivenessMatch && interviewEffectivenessMatch[1]) {
            effectivenessProbability = parseFloat(interviewEffectivenessMatch[1].replace(/%/, ''));
          }
        }
      } catch (e) {
        // Fallback to regex extraction from plain text
        const cleanedInput = ieResultText.replace(/\*\*/g, '').trim();
        const effectivenessRegex = /Interview Effectiveness:\s*(\d+(\.\d+)?%)/;
        const interviewEffectivenessMatch = cleanedInput.match(effectivenessRegex);
        if (interviewEffectivenessMatch && interviewEffectivenessMatch[1]) {
          effectivenessProbability = parseFloat(interviewEffectivenessMatch[1].replace(/%/, ''));
        }
      }
      
      setInterviewEffectiveness(effectivenessProbability);

      // Save to database using force endpoint since we're bypassing content check
      const insertData = {
        project: projectName,
        candidate: candidateName,
        interviewer: interviewerName,
        assessment_probability: resultText,
        assessment_effectiveness: ieResultText,
        shortlisting_probability: skillProbability,
        interview_effectiveness: effectivenessProbability,
        intext_data: selectedSkill,
        jobDescription: jdContent,
        transcript: candidateContent
      };

      const insertResponse = await apiFetch('/api/assessment/force', {
        method: 'POST',
        body: JSON.stringify(insertData),
      });

      if (insertResponse.ok) {
        console.log('Assessment saved successfully (forced)');
      } else {
        console.warn('Save failed, but assessment completed');
      }

    } catch (error) {
      console.error('Assessment error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="interview-assessment-container">
      {/* Progress Tracker */}
      <div className="progress-tracker">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-indicator">1</div>
          <div className="step-label">Provide Details</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-indicator">2</div>
          <div className="step-label">Upload Job Description</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
          <div className="step-indicator">3</div>
          <div className="step-label">Upload Interview Transcript</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 4 ? 'active' : ''} ${currentStep > 4 ? 'completed' : ''}`}>
          <div className="step-indicator">4</div>
          <div className="step-label">Generate Analysis</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 5 ? 'active' : ''}`}>
          <div className="step-indicator">5</div>
          <div className="step-label">View Results</div>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="error-notification">
          <FiAlertTriangle size={20} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="interview-assessment-content">
        <div className="upload-section-container">
          <div className="upload-card">
            <div className="card-header">
              <h3>Interview Assessment</h3>
            </div>
            <div className="card-body">
              <div className="upload-areas-container">
                

                {/* Details Section - New form fields in a group */}
                                <div className="details-section">
                                  {/* Project Name Input */}
                                  <div className="form-field">
                                    <div className="field-icon-container">
                                      <FiBriefcase size={18} className="field-icon" />
                                    </div>
                                    <input
                                      type="text"
                                      placeholder="Project Name *"
                                      value={projectName}
                                      onChange={(e) => handleInputChange('project', e.target.value)}
                                      className={`form-input ${projectName.trim() === '' && currentStep >= 2 ? 'input-error' : ''}`}
                                      required
                                    />
                                  </div>
                
                                  {/* Candidate Name Input */}
                                  <div className="form-field">
                                    <div className="field-icon-container">
                                      <FiUser size={18} className="field-icon" />
                                    </div>
                                    <input
                                      type="text"
                                      placeholder="Candidate Name *"
                                      value={candidateName}
                                      onChange={(e) => handleInputChange('candidate', e.target.value)}
                                      className={`form-input ${candidateName.trim() === '' && currentStep >= 2 ? 'input-error' : ''}`}
                                      required
                                    />
                                  </div>
                
                                  {/* Interviewer Name Input */}
                                  <div className="form-field">
                                    <div className="field-icon-container">
                                      <FiUsers size={18} className="field-icon" />
                                    </div>
                                    <input
                                      type="text"
                                      placeholder="Interviewer Name *"
                                      value={interviewerName}
                                      onChange={(e) => handleInputChange('interviewer', e.target.value)}
                                      className={`form-input ${interviewerName.trim() === '' && currentStep >= 2 ? 'input-error' : ''}`}
                                      required
                                    />
                                  </div>
                
                                  {/* Interview Type Selection (moved here from standalone section) */}
                                  <div className="form-field">
                                    <div className="interview-type-selector" ref={dropdownRef}>
                                      <div
                                        className={`selector-dropdown ${selectedSkill ? 'selected' : ''} ${!selectedSkill && currentStep >= 2 ? 'input-error' : ''}`}
                                        onClick={() => setShowDropdown(!showDropdown)}
                                      >
                                        <span>{selectedSkill || 'Select Interview Type *'}</span>
                                        <span className="dropdown-arrow">▼</span>
                                      </div>
                                      {showDropdown && (
                                        <div className="dropdown-options">
                                          {intExt.map((skill) => (
                                            <div
                                              key={skill}
                                              className="dropdown-option"
                                              onClick={() => handleSkillSelect(skill)}
                                            >
                                              {skill}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

{/* JD Upload Area */}
                <div className="upload-area">
                  <h4 className="upload-area-title">Job Description</h4>
                  {!jdFile ? (
                    <div
                      className={`file-drop-area ${isDraggingJd ? 'dragging' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'jd')}
                      onDragLeave={() => handleDragLeave('jd')}
                      onDrop={(e) => handleDrop(e, 'jd')}
                      onClick={() => jdInputRef.current.click()}
                    >
                      <div className="upload-icon">
                        <FiUpload size={28} color="#1976d2" />
                      </div>
                      <div className="upload-text">
                        <p className="upload-subtitle">
                          Drop your job description here, or <span className="browse-text">browse</span>
                        </p>
                        <p className="file-types">Supported formats: .txt, .docx</p>
                      </div>
                      <input
                        ref={jdInputRef}
                        id="jd-upload"
                        type="file"
                        accept=".txt,.docx"
                        onChange={handleJdFileChange}
                        className="file-input"
                      />
                    </div>
                  ) : (
                    <div className="file-preview">
                      <div className="file-info">
                        <div className="file-icon">
                          <FiFileText size={24} color="#1976d2" />
                        </div>
                        <div className="file-details">
                          <p className="file-name">{jdFile.name}</p>
                          <p className="file-size">{(jdFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          className="icon-button preview-btn"
                          title="Preview content"
                          onClick={() => alert(jdContent.substring(0, 500) + "...")}
                        >
                          <FiSearch size={18} />
                        </button>
                        <button
                          className="icon-button remove-btn"
                          title="Remove file"
                          onClick={() => removeFile('jd')}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Candidate Transcript Upload Area */}
                <div className="upload-area">
                  <h4 className="upload-area-title">Interview Transcript</h4>
                  {!candidateFile ? (
                    <div
                      className={`file-drop-area ${isDraggingTranscript ? 'dragging' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'transcript')}
                      onDragLeave={() => handleDragLeave('transcript')}
                      onDrop={(e) => handleDrop(e, 'transcript')}
                      onClick={() => candidateInputRef.current.click()}
                    >
                      <div className="upload-icon">
                        <FiUpload size={28} color="#1976d2" />
                      </div>
                      <div className="upload-text">
                        <p className="upload-subtitle">
                          Drop your interview transcript here, or <span className="browse-text">browse</span>
                        </p>
                        <p className="file-types">Supported formats: .txt, .docx</p>
                      </div>
                      <input
                        ref={candidateInputRef}
                        id="candidate-upload"
                        type="file"
                        accept=".txt,.docx"
                        onChange={handleCandidateFileChange}
                        className="file-input"
                      />
                    </div>
                  ) : (
                    <div className="file-preview">
                      <div className="file-info">
                        <div className="file-icon">
                          <FiFileText size={24} color="#1976d2" />
                        </div>
                        <div className="file-details">
                          <p className="file-name">{candidateFile.name}</p>
                          <p className="file-size">{(candidateFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <div className="file-actions">
                        <button
                          className="icon-button preview-btn"
                          title="Preview content"
                          onClick={() => alert(candidateContent.substring(0, 500) + "...")}
                        >
                          <FiSearch size={18} />
                        </button>
                        <button
                          className="icon-button remove-btn"
                          title="Remove file"
                          onClick={() => removeFile('candidate')}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className={`btn btn-primary ${!isAssessButtonEnabled ? 'disabled' : ''}`}
              onClick={handleAssessClick}
              disabled={!isAssessButtonEnabled || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Analyzing...
                </>
              ) : (
                <>Assess Interview</>
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleReset}
            >
              <FiRotateCw size={16} />
              Reset
            </button>
          </div>
        </div>

        {/* Gauges for Visualization */}
        <div className="results-section">
          <div className="gauges-container">
            {/* Shortlisting Probability Gauge */}
            <div className="gauge-card">
              <h3 className="gauge-title">Candidate Shortlisting</h3>
              <div className="gauge-visualization">
                <Speedometer
                  maxValue={100}
                  value={shortlistingProbability || 0}
                  needleColor="#333333"
                  startColor="#FF5252"
                  endColor="#4CAF50"
                  segments={3}
                  segmentColors={["#FF5252", "#FFC107", "#4CAF50"]}
                  currentValueText={`${shortlistingProbability || 0}%`}
                  textColor="#212121"
                  valueTextFontSize="20px"
                  needleTransitionDuration={500}
                  ringWidth={25}
                  height={200}
                  width={280}
                />
              </div>
              {shortlistingProbability && (
                <div className={`gauge-recommendation ${shortlistingProbability >= 75 ? 'high' :
                    shortlistingProbability >= 50 ? 'medium' : 'low'
                  }`}>
                  <div className="recommendation-icon">
                    {shortlistingProbability >= 50 ?
                      <FiCheckCircle size={18} /> :
                      <FiAlertTriangle size={18} />
                    }
                  </div>
                  <div className="recommendation-text">
                    {getRecommendationText(shortlistingProbability, 'shortlisting')}
                  </div>
                </div>
              )}
            </div>

            {/* Interview Effectiveness Gauge */}
            <div className="gauge-card">
              <h3 className="gauge-title">Interview Effectiveness</h3>
              <div className="gauge-visualization">
                <Speedometer
                  maxValue={100}
                  value={interviewEffectiveness || 0}
                  needleColor="#333333"
                  startColor="#FF5252"
                  endColor="#4CAF50"
                  segments={3}
                  segmentColors={["#FF5252", "#FFC107", "#4CAF50"]}
                  currentValueText={`${interviewEffectiveness || 0}%`}
                  textColor="#212121"
                  valueTextFontSize="20px"
                  needleTransitionDuration={500}
                  ringWidth={25}
                  height={200}
                  width={280}
                />
              </div>
              {interviewEffectiveness && (
                <div className={`gauge-recommendation ${interviewEffectiveness >= 75 ? 'high' :
                    interviewEffectiveness >= 50 ? 'medium' : 'low'
                  }`}>
                  <div className="recommendation-icon">
                    {interviewEffectiveness >= 50 ?
                      <FiCheckCircle size={18} /> :
                      <FiAlertTriangle size={18} />
                    }
                  </div>
                  <div className="recommendation-text">
                    {getRecommendationText(interviewEffectiveness, 'effectiveness')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Results */}
      {(isLoading || assessmentResult || error) && (
        <div className="assessment-results-container">
          {/* <div className="results-header">
            <h2 className="results-title">Detailed Assessment Results</h2>
            
            
            {assessmentResult && (
              <div className="display-mode-toggle">
                <button 
                  className={`toggle-btn ${useStructuredUI ? 'active' : ''}`}
                  onClick={() => setUseStructuredUI(true)}
                >
                  Structured View
                </button>
                <button 
                  className={`toggle-btn ${!useStructuredUI ? 'active' : ''}`}
                  onClick={() => setUseStructuredUI(false)}
                >
                  Markdown View
                </button>
              </div>
            )}
          </div> */}

          {isLoading ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div className="loader-text">Analyzing interview data...</div>
            </div>
          ) : error ? (
            <div className="error-container">
              <FiAlertTriangle size={28} />
              <h3>Error</h3>
              <p>{error}</p>
            </div>
          ) : assessmentResult ? (
            <div className="tabs-container">
              <div className="tabs">
                <div
                  onClick={() => setSelectedTab('results')}
                  className={`tab ${selectedTab === 'results' ? 'active' : ''}`}
                >
                  Skills Assessment
                </div>
                <div
                  onClick={() => setSelectedTab('details')}
                  className={`tab ${selectedTab === 'details' ? 'active' : ''}`}
                >
                  Interview Effectiveness
                </div>
              </div>
              <div className="tab-content">
                {selectedTab === 'results' ? (
                  <HybridRendererSelector 
                    assessmentData={assessmentResult} 
                    type="skills" 
                    useStructured={useStructuredUI}
                  />
                ) : (
                  <HybridRendererSelector 
                    assessmentData={effectivenessResult} 
                    type="effectiveness" 
                    useStructured={useStructuredUI}
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Duplicate Entry Modal */}
      {showDuplicateModal && duplicateError && (
        <div className="modal-overlay">
          <div className="duplicate-modal">
            {!viewingExistingAssessment ? (
              <>
                <div className="modal-header">
                  <h3>Duplicate Assessment Found</h3>
                  <FiAlertTriangle size={24} color="#FF9800" />
                </div>
                
                <div className="modal-body">
                  <p className="duplicate-message">
                    {duplicateError.message}
                  </p>
                  
                  {existingAssessment && (
                    <div className="existing-assessment-info">
                      <h4>Existing Assessment Details:</h4>
                      <ul>
                        <li><strong>Created:</strong> {new Date(existingAssessment.create_date).toLocaleDateString()}</li>
                        <li><strong>Shortlisting Probability:</strong> {existingAssessment.shortlisting_probability}%</li>
                        <li><strong>Interview Effectiveness:</strong> {existingAssessment.interview_effectiveness}%</li>
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="modal-actions">
                  {/* <button 
                    className="btn btn-primary"
                    onClick={() => handleViewExisting()}
                  >
                    View Existing
                  </button> */}
                  
                  <button 
                    className="btn btn-cancel"
                    onClick={() => setShowDuplicateModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <h3>Existing Assessment Details</h3>
                  <button 
                    onClick={() => setViewingExistingAssessment(null)}
                    className="back-button"
                  >
                    ← Back
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="assessment-details">
                    <h4>Assessment Results:</h4>
                    <div className="assessment-content">
                      {/* Display the full assessment here */}
                      <p><strong>Shortlisting Probability:</strong> {viewingExistingAssessment.shortlisting_probability}%</p>
                      <p><strong>Interview Effectiveness:</strong> {viewingExistingAssessment.interview_effectiveness}%</p>
                      <p><strong>Created:</strong> {new Date(viewingExistingAssessment.create_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    className="btn btn-cancel"
                    onClick={() => {
                      setViewingExistingAssessment(null);
                      setShowDuplicateModal(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content Duplicate Modal - Improved Inline Version */}
      {showContentDuplicateModal && contentDuplicateError && (
        <div className="modal-overlay">
          <div className="duplicate-modal content-duplicate-modal">
            <div className="modal-header">
              <h3>Similar Content Detected</h3>
              <FiAlertTriangle size={20} color="#FF9800" />
            </div>
            
            <div className="modal-body">
              <p className="duplicate-message">
                {contentDuplicateError.message}
              </p>
              
              <div className="existing-entries-list">
                <h4>Existing Assessments with Same Content:</h4>
                {existingContentEntries.map((entry, index) => (
                  <div key={entry.id} className="existing-entry-item-inline">
                    <div className="entry-header">
                      <strong>Assessment {index + 1}</strong>
                      <span className="entry-date">
                        {new Date(entry.create_date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="entry-details">
                      <div className="entry-info-inline">
                        <div className="info-row">
                          <span className="label">Project:</span>
                          <span className="value">{entry.project}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Candidate:</span>
                          <span className="value">{entry.candidate}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Interviewer:</span>
                          <span className="value">{entry.interviewer}</span>
                        </div>
                      </div>
                      
                      <div className="assessment-scores">
                        <div className="score-item">
                          <span className="score-label">Shortlisting:</span>
                          <span className="score-value">{entry.shortlisting_probability}%</span>
                        </div>
                        <div className="score-item">
                          <span className="score-label">Effectiveness:</span>
                          <span className="score-value">{entry.interview_effectiveness}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="modal-notice">
                <p><strong>Note:</strong> You can not proceed with this assessment, cancel to avoid duplicate processing.</p>
              </div>
            </div>
            
            <div className="modal-actions">
              {/* <button 
                className="btn btn-primary"
                onClick={handleProceedAnyway}
              >
                Proceed with New Assessment
              </button> */}
              
              <button 
                className="btn btn-cancel"
                onClick={() => setShowContentDuplicateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewUser;